import { readerDataStore } from "./../../../hookstate/readerDataStore";
import { useQuery } from "@tanstack/react-query";
import {
  GET_COMIC_RELEASES_QUERY_KEY,
  GET_RELEASE_FRAGMENTS_QUERY_KEY,
  getComicReleases,
  getRelease,
  getReleaseFragments,
} from "../api/readerScreenApi";
import { useEffect, useState } from "react";
import { useGaTracking } from "../../../hooks/useGaTracking";
import { useReleaseDataQueryKey } from "./useReleaseDataQueryKey";
import { AxiosError } from "axios";
import { useHookstate } from "@hookstate/core";
import { useEntityCache } from "../../../hooks/useEntityCache";
import { languagesFilterStore } from "../../../hookstate/languageFiltersStore";
import { appStore } from "../../../hookstate/appStore";
import { readerNavigationContext } from "../../../hookstate/readerNavigationContextStore";
import { readerComponentsStore } from "../../../hookstate/readerComponentStore";

export function useHydrateReaderData(uuid: string) {
  const initData = useHookstate(appStore.init).get();
  const { language } = useHookstate(languagesFilterStore).get();
  const navigationContext = readerNavigationContext.get();

  const { addThirdPartyGaId } = useGaTracking();
  const { saveEntityToCache } = useEntityCache();
  const releaseDataQueryKey = useReleaseDataQueryKey();

  const [shouldGetFragments, setShouldGetFragments] = useState(false);

  const { data: releaseData, error } = useQuery({
    enabled: !!uuid && !!initData.app_urls,
    queryKey: releaseDataQueryKey,
    queryFn: async () => {
      const responseData = await getRelease(uuid, navigationContext);

      const release = responseData?.data.payload.results;

      if (release) {
        const comic = release.comic;
        const artist = comic?.artist;

        if (artist?.ga_id) {
          addThirdPartyGaId(artist.name, artist.ga_id);
        }

        readerDataStore.merge({
          releaseData: release,
          releasePagesData: release.page_objects,
          comicData: comic,
        });

        saveEntityToCache(release);
        saveEntityToCache(comic);
        saveEntityToCache(artist);

        const shouldGetFragments = !!(release.is_augmented || release.can_edit);
        if (!shouldGetFragments) {
          readerDataStore.releaseFragmentsData.set(undefined);
          readerDataStore.loaded.set(true);
        }
        setShouldGetFragments(shouldGetFragments);
      }

      return responseData;
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (error) {
      readerDataStore.responseError.set(error as AxiosError);
      readerComponentsStore.isOverlayOpen.set(true);
    }
  }, [error]);

  const comicId = releaseData?.data.payload.results.comic?.id ?? 0;
  const langId = releaseData?.data.payload.results.lang_id ?? "en";
  const releaseId = releaseData?.data.payload.results.id ?? 0;

  const getReleasesQueryKey = [
    ...GET_COMIC_RELEASES_QUERY_KEY,
    comicId,
    language ?? langId,
  ];
  const getFragmentsQueryKey = [...GET_RELEASE_FRAGMENTS_QUERY_KEY, releaseId];

  useQuery({
    enabled: !!(comicId && langId),
    queryKey: getReleasesQueryKey,
    queryFn: async () => {
      const response = await getComicReleases(
        comicId,
        language ?? langId ?? "",
      );

      const sortedReleases = response?.data.payload.results.sort(
        (a, b) => a.order - b.order,
      );
      readerDataStore.comicReleasesData.set(sortedReleases);

      return response;
    },
    refetchOnWindowFocus: false,
  });

  useQuery({
    enabled: !!releaseId && shouldGetFragments,
    queryKey: getFragmentsQueryKey,
    queryFn: async () => {
      const response = await getReleaseFragments(releaseId);

      const releaseFragments = response?.data.payload.results;
      readerDataStore.releaseFragmentsData.set(releaseFragments);
      readerDataStore.loaded.set(true);

      return response;
    },
    refetchOnWindowFocus: false,
  });
}
